import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import routes from 'constants/routes'
import type { AiContextType, AiRouteContext } from 'services/aiService'

/**
 * Which asset the user is looking at, for Ask MDT (MYD-622): `{ type, id }` or
 * null. On a project page, "what's overdue here?" then means that project.
 *
 * Only the two values are sent; the server reads the asset again through the
 * user's own access before the model hears of it (backend/ai/context.js), so a
 * hand-edited URL can name nothing the user could not open.
 *
 * The server knows four kinds of asset (location, project, portfolio,
 * evaluation), so every screen maps to one of those or to null:
 *
 *   /locations/:id/projects/:projectId/*   the project (the narrower asset)
 *   /locations/:id/*                       the location
 *   /projects/:id/*                        the project
 *   /portfolios/:id/*                      the portfolio
 *   /evaluations/:id, its tabs and /map    the evaluation
 *   /explorer/:grain?scopeType=&scopeId=   the asset a scoped report is about
 *
 * And deliberately null:
 *
 *   /evaluation-sites/:siteId   a site may belong to no evaluation, or several,
 *                               so there is no one asset to name
 *   /presentations/:id/edit     a deck spans assets; its stories name them
 *   an unscoped Explorer grid, and everything else
 *
 * An id that is not a Mongo id (`/locations/new`) is not an asset.
 */

const OBJECT_ID = /^[a-f0-9]{24}$/i
const CONTEXT_TYPES: readonly AiContextType[] = ['location', 'project', 'portfolio', 'evaluation']

/** First match wins, so the narrower pattern comes first. */
const PATTERNS: ReadonlyArray<{ path: string; type: AiContextType; param: string }> = [
	{ path: `${routes.LOCATIONS}/:id/projects/:projectId/*`, type: 'project', param: 'projectId' },
	{ path: `${routes.LOCATIONS}/:id/*`, type: 'location', param: 'id' },
	{ path: `${routes.PROJECTS}/:id/*`, type: 'project', param: 'id' },
	{ path: `${routes.PORTFOLIOS}/:id/*`, type: 'portfolio', param: 'id' },
	{ path: `${routes.EVALUATIONS}/:id/*`, type: 'evaluation', param: 'id' },
]

/** The asset a route is about, or null. Pure, for tests. */
export const routeAssetContext = (pathname: string, search = ''): AiRouteContext | null => {
	for (const { path, type, param } of PATTERNS) {
		const match = matchPath(path, pathname)
		const id = match?.params[param]
		if (match) return id && OBJECT_ID.test(id) ? { type, id } : null
	}

	// A scoped Explorer report (MYD-598) is about the asset in its URL.
	if (matchPath(routes.EXPLORER_GRAIN, pathname)) {
		const params = new URLSearchParams(search)
		const type = params.get('scopeType') as AiContextType | null
		const id = params.get('scopeId')
		if (type && CONTEXT_TYPES.includes(type) && id && OBJECT_ID.test(id)) return { type, id }
	}
	return null
}

/** The asset on screen now, recomputed only when the URL changes. */
export const useRouteAssetContext = (): AiRouteContext | null => {
	const { pathname, search } = useLocation()
	return useMemo(() => routeAssetContext(pathname, search), [pathname, search])
}

export default useRouteAssetContext
